import { toast } from 'sonner';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  Archive,
  Copy,
  Download,
  FileText,
  Loader2,
  Mic,
  MoreHorizontal,
  Printer,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { EditorToolbar } from './editor/EditorToolbar';
import { DocumentTagPicker } from './DocumentTagPicker';
import { useDocumentEditor } from './editor/useDocumentEditor';
import { useEncounterAudioSegments } from '../api/encounters-api';
import { useTemplate } from '@/features/templates/api/templates-api';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { type GeneratedDocumentOut, useDocumentSources, useUpdateDocument } from '../api/documents-api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const statusColors: Record<string, string> = {
  exported: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-800',
  draft: 'bg-yellow-100 text-yellow-800',
  finalized: 'bg-green-100 text-green-800',
};

interface DocumentViewerSheetProps {
  document: GeneratedDocumentOut | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function DocumentViewerSheet({ open, document, onOpenChange }: DocumentViewerSheetProps) {
  const { data: template } = useTemplate(document?.template_id ?? undefined);
  const updateDocument = useUpdateDocument();
  const [isDirty, setIsDirty] = useState(false);

  // Source encounter(s) → audio playback. Most generated documents are
  // produced from exactly one encounter (the typical Quick Start path).
  const { data: sources = [] } = useDocumentSources(open ? document?.id : undefined);
  const encounterId = sources[0]?.encounter_id;
  const { data: audioSegments = [] } = useEncounterAudioSegments(open ? encounterId : undefined);

  const handleUpdate = useCallback(() => {
    setIsDirty(true);
  }, []);

  const editor = useDocumentEditor({
    onUpdate: handleUpdate,
    initialContent: document?.content ?? '',
  });

  if (!document) return null;

  const handleCopy = () => {
    const content = editor ? editor.storage.markdown.getMarkdown() : document.content;
    navigator.clipboard.writeText(content);
    toast.success('Document content copied to clipboard');
  };

  const handleSave = async () => {
    if (!editor) return;
    const content = editor.storage.markdown.getMarkdown();
    try {
      await updateDocument.mutateAsync({ data: { content }, documentId: document.id });
      setIsDirty(false);
      toast.success('Document saved');
    } catch {
      toast.error('Failed to save document');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Hide the default `SheetPrimitive.Close` (top-4 right-4) — we render
        // our own X next to the dropdown menu in the header below.
        className="flex flex-col gap-3 w-full sm:max-w-4xl p-6 [&>[data-slot=sheet-default-close]]:hidden">
        <SheetHeader className="gap-1 p-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <SheetTitle>{template?.name || 'Document'}</SheetTitle>
            </div>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleCopy}>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy content
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toast.info('Print feature coming soon')}>
                    <Printer className="mr-2 h-4 w-4" />
                    Print
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => toast.info('Download feature coming soon')}>
                    <Download className="mr-2 h-4 w-4" />
                    Export PDF
                  </DropdownMenuItem>
                  {document.status !== 'archived' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => toast.info('Archive feature coming soon')}>
                        <Archive className="mr-2 h-4 w-4" />
                        Archive
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => toast.info('Delete feature coming soon')}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <SheetClose asChild>
                <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Close">
                  <X className="h-4 w-4" />
                </Button>
              </SheetClose>
            </div>
          </div>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={`text-xs ${statusColors[document.status] ?? ''}`}>
              {document.status}
            </Badge>
            <span className="text-xs">
              Generated {format(new Date(document.generated_at), "MMM d, yyyy 'at' h:mm a")}
            </span>
          </SheetDescription>
        </SheetHeader>

        {/* Tags */}
        <div className="rounded-lg border p-3 shrink-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Tags</p>
          <DocumentTagPicker kind="generated" documentId={document.id} />
        </div>

        {/* Source recordings */}
        {audioSegments.length > 0 && (
          <div className="rounded-lg border p-3 shrink-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Mic className="h-3.5 w-3.5" />
              Source Recordings ({audioSegments.length})
            </p>
            <div className="space-y-2">
              {audioSegments.map((seg, idx) => (
                <div key={seg.id} className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Recording {idx + 1}
                    {seg.duration ? ` · ${formatDuration(seg.duration)}` : ''}
                  </p>
                  <audio controls preload="metadata" src={seg.audio_url} className="w-full h-8">
                    <track kind="captions" />
                  </audio>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Generation Parameters — primitives only; nested objects are skipped
            (they are workflow-internal audit data like the report pipeline
            snapshot, not user-facing). */}
        {document.additional_context && hasPrimitiveEntries(document.additional_context) && (
          <div className="rounded-lg border p-3 shrink-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Generation Parameters
            </p>
            <div className="space-y-1">
              {Object.entries(document.additional_context)
                .filter(([, v]) => isPrimitive(v))
                .map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-3 text-xs">
                    <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium truncate">{formatPrimitive(value)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        <Separator className="shrink-0" />

        {/* Editor area */}
        <div className="flex-1 overflow-y-auto rounded-md border">
          {/* Sticky toolbar */}
          <div className="sticky top-0 z-10 bg-background border-b">
            <EditorToolbar editor={editor} />
          </div>
          {/* Editor content */}
          <EditorContent editor={editor} />
        </div>

        {/* Save bar — only shown when dirty */}
        {isDirty && (
          <div className="border-t pt-3 pb-1 shrink-0 flex items-center justify-end gap-2">
            <Button size="sm" disabled={updateDocument.isPending} onClick={handleSave}>
              {updateDocument.isPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-3.5 w-3.5" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatPrimitive(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function hasPrimitiveEntries(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some(isPrimitive);
}

function isPrimitive(value: unknown): value is string | number | boolean {
  const t = typeof value;
  return t === 'string' || t === 'number' || t === 'boolean';
}
